import Share from "@/components/Share/Share";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link, useRouter } from "@tanstack/react-router";
import { shareDataFormatter } from "@/utils/challengeShare";
import { getDateRangeStatus } from "@/utils/challenge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ProfileIcon } from "@/assets/icons/Profile";
import { Progress } from "@/components/ui/progress";
import { ShareIcon } from "@/assets/icons/ShareIcon";
import { Currencies } from "@/store/currencyStore";
import { useEffect, useState } from "react";
import { SupportersDisplay } from "@/pages/community-challenge/components/SupportersDisplay";
import { ChallengeType } from "@/types/challenge";
import { useChallengeDonationData } from "@/pages/program/useDonationData";
import { formatNumber } from "@/utils/currency";
import { Trans } from "react-i18next";

type ActiveChallengeCardProps = {
  challenge: ChallengeType;
  handleSuccess?: () => void;
};
export const ActiveChallengeCard = ({ challenge, handleSuccess }: ActiveChallengeCardProps) => {
  const shareData = shareDataFormatter(challenge);
  const { t, currency, raisedPercentage } = useChallengeDonationData(challenge);
  const { navigate } = useRouter();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (challenge?.program?.main_image?.large_url) {
      const img = new Image();
      img.src = challenge.program.main_image.large_url;
      img.onload = () => {
        setFadeOut(true);
        setImageLoaded(true);
      };
    }
  }, [challenge]);

  const handleDonateNow = () => {
    if (challenge) {
      navigate({
        to: `/challenge-payment/${challenge.id}`,
        search: { backUrl: location.pathname },
      });
    }
  };

  return (
    <Card
      className={cn(
        "group cursor-pointer shrink-0 flex flex-col justify-between rounded-2xl border-background-second min-h-[480px] w-[295px] pb-0 overflow-hidden bg-transparent",
      )}
    >
      <Link
        to={`/challenge/${challenge.id}`}
        aria-label="Go to challenge"
        className="h-full w-full"
      >
        <div className="relative overflow-hidden">
          {!challenge.program.published && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-70">
              <span className="text-xl font-bold text-white">{t("ProgramCard.preview_label")}</span>
            </div>
          )}
          {challenge.challenge_picture ? (
            <img
              src={challenge.challenge_picture?.large_url}
              srcSet={`
              ${challenge.challenge_picture?.small_url} 640w,
              ${challenge.challenge_picture?.medium_url} 1024w,
              ${challenge.challenge_picture?.large_url} 1200w
            `}
              sizes="(min-width: 1024px) 1200px, (min-width: 640px) 1024px, 640px"
              alt="Product Image"
              className={`a w-full rounded-t-2xl object-cover transition-all duration-1000 group-hover:scale-105 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
            />
          ) : (
            <img
              src={challenge.program.main_image?.large_url}
              srcSet={`
              ${challenge.program.main_image?.small_url} 640w,
              ${challenge.program.main_image?.medium_url} 1024w,
              ${challenge.program.main_image?.large_url} 1200w
            `}
              sizes="(min-width: 1024px) 1200px, (min-width: 640px) 1024px, 640px"
              alt="Product Image"
              className={`a w-full rounded-t-2xl object-cover transition-all duration-1000 group-hover:scale-105 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
            />
          )}
          {!imageLoaded && (
            <div
              className={`absolute inset-0 blur-md transition-opacity duration-1000 ${
                fadeOut ? "opacity-0" : "opacity-100"
              }`}
              style={{
                backgroundImage: `url(${challenge.program.main_image?.thumbnail_url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <div className="absolute left-0 top-0 flex h-full w-full flex-col items-start justify-between p-6">
            <div className="flex flex-row gap-2 font-bold text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 1.5C8.55229 1.5 9 1.94772 9 2.5V3.5H15V2.5C15 1.94772 15.4477 1.5 16 1.5C16.5523 1.5 17 1.94772 17 2.5V3.50163C17.4755 3.50489 17.891 3.51471 18.2518 3.54419C18.8139 3.59012 19.3306 3.68868 19.816 3.93597C20.5686 4.31947 21.1805 4.93139 21.564 5.68404C21.8113 6.16937 21.9099 6.68608 21.9558 7.24817C22 7.78936 22 8.45372 22 9.25868V17.7413C22 18.5463 22 19.2106 21.9558 19.7518C21.9099 20.3139 21.8113 20.8306 21.564 21.316C21.1805 22.0686 20.5686 22.6805 19.816 23.064C19.3306 23.3113 18.8139 23.4099 18.2518 23.4558C17.7106 23.5 17.0463 23.5 16.2413 23.5H7.75868C6.95372 23.5 6.28936 23.5 5.74817 23.4558C5.18608 23.4099 4.66937 23.3113 4.18404 23.064C3.43139 22.6805 2.81947 22.0686 2.43597 21.316C2.18868 20.8306 2.09012 20.3139 2.04419 19.7518C1.99998 19.2106 1.99999 18.5463 2 17.7413V9.2587C1.99999 8.45373 1.99998 7.78937 2.04419 7.24817C2.09012 6.68608 2.18868 6.16937 2.43597 5.68404C2.81947 4.93139 3.43139 4.31947 4.18404 3.93597C4.66937 3.68868 5.18608 3.59012 5.74818 3.54419C6.10898 3.51471 6.52454 3.50489 7 3.50163V2.5C7 1.94772 7.44772 1.5 8 1.5ZM7 5.50176C6.55447 5.50489 6.20463 5.51356 5.91104 5.53755C5.47262 5.57337 5.24842 5.6383 5.09202 5.71799C4.7157 5.90973 4.40973 6.21569 4.21799 6.59202C4.1383 6.74842 4.07337 6.97262 4.03755 7.41104C4.00078 7.86113 4 8.44342 4 9.3V9.5H20V9.3C20 8.44342 19.9992 7.86113 19.9624 7.41104C19.9266 6.97262 19.8617 6.74842 19.782 6.59202C19.5903 6.2157 19.2843 5.90973 18.908 5.71799C18.7516 5.6383 18.5274 5.57337 18.089 5.53755C17.7954 5.51356 17.4455 5.50489 17 5.50176V6.5C17 7.05228 16.5523 7.5 16 7.5C15.4477 7.5 15 7.05228 15 6.5V5.5H9V6.5C9 7.05228 8.55229 7.5 8 7.5C7.44772 7.5 7 7.05228 7 6.5V5.50176ZM20 11.5H4V17.7C4 18.5566 4.00078 19.1389 4.03755 19.589C4.07337 20.0274 4.1383 20.2516 4.21799 20.408C4.40973 20.7843 4.7157 21.0903 5.09202 21.282C5.24842 21.3617 5.47262 21.4266 5.91104 21.4624C6.36113 21.4992 6.94342 21.5 7.8 21.5H16.2C17.0566 21.5 17.6389 21.4992 18.089 21.4624C18.5274 21.4266 18.7516 21.3617 18.908 21.282C19.2843 21.0903 19.5903 20.7843 19.782 20.408C19.8617 20.2516 19.9266 20.0274 19.9624 19.589C19.9992 19.1389 20 18.5566 20 17.7V11.5Z"
                  fill="#EDEAE4"
                />
              </svg>
              {getDateRangeStatus(challenge.start_date as string, challenge.end_date as string)}
            </div>
            <div className="flex flex-row items-center gap-2 font-[650] text-white">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#B4B4B4]">
                <ProfileIcon height={20} width={19} />
              </div>
              {challenge.is_owner ? (
                <p className="foont-[650] text-sm text-white">
                  <Trans i18nKey={"challenge.createdBy"} values={{ person: t("challenge.me") }} />
                </p>
              ) : (
                <p className="foont-[650] text-sm text-white">
                  <Trans
                    i18nKey={"challenge.createdBy"}
                    values={{
                      person: `${challenge.owner.profile.firstName}
                      ${challenge.owner.profile.lastName}`,
                    }}
                  />
                </p>
              )}
            </div>
          </div>
        </div>
        <CardContent>
          <div className="flex w-full flex-col gap-4 pt-6">
            <h3 className="scroll-m-20 text-2xl font-[650] tracking-tight">{challenge.name}</h3>
            <SupportersDisplay
              count={challenge.supporters_count}
              supporters={challenge.supporters}
              dark
            />
            <div className="flex flex-col items-start gap-2">
              <Progress
                value={raisedPercentage}
                className="dark h-[4px] rounded-[2px] rtl:scale-x-[-1] rtl:transform"
                aria-label="Donation amount progress bar"
              />
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-normal text-black">
                  {t("ProgramCard.goal_label")}
                </span>
                <p className="text-sm font-[650]">
                  {currency === Currencies.EGP
                    ? `EGP ${formatNumber(challenge.goal_egp)} `
                    : `$ ${formatNumber(challenge.goal_usd)}`}
                  ({raisedPercentage}%)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Link>
      <CardFooter className="gap-2 !p-2">
        {challenge.is_owner ? (
          <Link to={`/challenge/${challenge.id}`} aria-label="Go to challenge" className="w-full">
            <Button
              variant="custom"
              className={
                "h-full w-full flex-grow rounded-md bg-foreground py-4 !text-base font-bold text-white sm:text-sm md:px-1"
              }
            >
              {t("challenge.edit")}
            </Button>
          </Link>
        ) : (
          <Button
            variant="custom"
            className={
              "h-full w-full flex-grow rounded-md py-4 !text-base font-bold sm:text-sm md:px-1"
            }
            onClick={handleDonateNow}
          >
            {t("ProgramCard.donate_now")}
          </Button>
        )}
        <Share shareData={shareData} programId={challenge.id} onSuccess={handleSuccess}>
          <ShareIcon className="fill-[#22222A]" />
        </Share>
      </CardFooter>
    </Card>
  );
};
